import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { getUsers, createUser, updateUser, getRoles } from "../../services/api";
import { User, Role } from "../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "./AdminLayout";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";

interface UserFormData {
  username: string;
  password: string;
  confirmPassword: string;
  roleNames: string[];
}

const UserForm: React.FC = () => {
  const { language } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<UserFormData>();

  const password = watch("password");

  useEffect(() => {
    fetchRoles();
    
    if (isEditMode) {
      fetchUser();
    }
  }, [id, isEditMode]);

  const fetchRoles = async () => {
    try {
      const response = await getRoles();
      setRoles(response.data);
      setAvailableRoles(response.data.map((role: Role) => role.name));
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("حدث خطأ أثناء جلب بيانات الصلاحيات");
    }
  };

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      const response = await getUsers();
      const user = response.data.find((u: User) => u.id === parseInt(id!));

      if (user) {
        reset({
          username: user.username,
          password: "",
          confirmPassword: "",
          roleNames: user.roleNames,
        });
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      toast.error("حدث خطأ أثناء جلب بيانات المستخدم");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    // Validate passwords
    if (!isEditMode && !data.password) {
      toast.error("كلمة المرور مطلوبة للمستخدمين الجدد");
      return;
    }

    if (data.password && data.password !== data.confirmPassword) {
      toast.error("كلمات المرور غير متطابقة");
      return;
    }

    setIsLoading(true);

    try {
      if (isEditMode) {
        // For update - only include password if it was provided
        const updatePayload: any = {
          username: data.username,
          roleNames: data.roleNames,
        };
        
        // Only include password if it was provided (not empty)
        if (data.password && data.password.trim() !== "") {
          updatePayload.password = data.password;
        }

        await updateUser(parseInt(id!), updatePayload);
        toast.success("تم تحديث بيانات المستخدم بنجاح");
      } else {
        // For create - password is required
        const createPayload = {
          username: data.username,
          password: data.password,
          roleNames: data.roleNames,
        };

        await createUser(createPayload);
        toast.success("تم إضافة المستخدم بنجاح");
      }

      navigate("/admin/users");
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(getTranslation("saveError", language));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-mauritania-green-dark">
            {isEditMode ? "تعديل بيانات المستخدم" : getTranslation("addUser", language)}
          </h1>
          <button
            onClick={() => navigate("/admin/users")}
            className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />{getTranslation("back", language)}</button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* Username Input */}
            <div className="mb-6">
              <label htmlFor="username" className="block text-gray-700 mb-2">
                اسم المستخدم *
              </label>
              <input
                id="username"
                type="text"
                {...register("username", { 
                  required: "هذا الحقل مطلوب",
                  minLength: {
                    value: 3,
                    message: "اسم المستخدم يجب أن يكون 3 أحرف على الأقل"
                  }
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.username ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.username && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.username.message}
                </p>
              )}
            </div>

            {/* Password Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="password" className="block text-gray-700 mb-2">
                  كلمة المرور {!isEditMode && "*"}
                  {isEditMode && (
                    <span className="text-sm text-gray-500 mr-2">
                      (اتركه فارغاً إذا لم ترد التغيير)
                    </span>
                  )}
                </label>
                <input
                  id="password"
                  type="password"
                  {...register("password", {
                    required: !isEditMode ? "هذا الحقل مطلوب" : false,
                    minLength: {
                      value: 3,
                      message: "كلمة المرور يجب أن تكون 3 أحرف على الأقل",
                    },
                    validate: (value) => {
                      if (isEditMode && value === "") return true; // Allow empty in edit mode
                      if (!isEditMode && !value) return "هذا الحقل مطلوب";
                      return value.length >= 3 || "كلمة المرور يجب أن تكون 3 أحرف على الأقل";
                    }
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={isEditMode ? "اتركه فارغاً للحفاظ على كلمة المرور الحالية" : ""}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-gray-700 mb-2"
                >
                  تأكيد كلمة المرور {!isEditMode && "*"}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  {...register("confirmPassword", {
                    required: !isEditMode ? "هذا الحقل مطلوب" : false,
                    validate: (value) => {
                      if (isEditMode && password === "") return true; // Skip validation if password is empty in edit mode
                      return value === password || "كلمات المرور غير متطابقة";
                    },
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                    errors.confirmPassword ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={isEditMode ? "اتركه فارغاً إذا لم ترد تغيير كلمة المرور" : ""}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>

            {/* Roles Selection */}
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">الصلاحيات *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableRoles.map((role) => (
                  <label key={role} className="flex items-center">
                    <input
                      type="checkbox"
                      value={role}
                      {...register("roleNames", {
                        required: "يجب اختيار صلاحية واحدة على الأقل",
                        validate: (value) => 
                          value.length > 0 || "يجب اختيار صلاحية واحدة على الأقل"
                      })}
                      className="ml-2 text-mauritania-green focus:ring-mauritania-green"
                    />
                    <span className="text-gray-700">{role}</span>
                  </label>
                ))}
              </div>
              {errors.roleNames && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.roleNames.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faSave} className="ml-2" />
                {isLoading ? getTranslation("saving", language) : getTranslation("save", language)}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default UserForm;